import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { XCircle } from 'lucide-react';

import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { ActionButton } from '@/shared/ui/action-button';
import { Textarea } from '@/components/ui/textarea';
import { Spinner } from '@/components/ui/spinner';
import { FormField, FormSelect } from '@/shared/ui';
import { SERVICE_REQUEST_REJECT_REASONS } from '@/entities/service-request';

import { useRejectServiceRequestForm } from '../model';

interface RejectServiceRequestModalProps {
  isOpen: boolean;
  onClose: () => void;
  serviceRequestId: number;
  trackIdnf?: string;
}

export const RejectServiceRequestModal = ({
  isOpen,
  onClose,
  serviceRequestId,
  trackIdnf
}: RejectServiceRequestModalProps) => {
  const { t } = useTranslation();

  const { form, onSubmit, isSubmitting } = useRejectServiceRequestForm({ serviceRequestId, onClose });
  const {
    control,
    register,
    formState: { errors }
  } = form;

  const reasonOptions = useMemo(
    () =>
      Object.values(SERVICE_REQUEST_REJECT_REASONS).map((value) => ({
        value,
        label: t(`requestHistory.cancellationReason.${value}`)
      })),
    [t]
  );

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle>
            {t('cmrlist.reject.onlinesrf.label')}
            {trackIdnf ? ` — ${trackIdnf}` : ''}
          </DialogTitle>
        </DialogHeader>

        <form id="reject-service-request-form" onSubmit={onSubmit} className="space-y-6 py-2">
          <FormSelect
            name="rejectReason"
            control={control}
            options={reasonOptions}
            label={t('cmrlist.reject.reason.label', 'Reason')}
            placeholder={t('cmrlist.reject.reason.placeholder', 'Select a reason')}
            error={errors.rejectReason?.message}
            className="w-full"
            required
          />

          <FormField
            label={t('cmrlist.reject.comment.label', 'Comment')}
            name="rejectComment"
            required
            error={errors.rejectComment?.message}>
            <Textarea
              id="rejectComment"
              {...register('rejectComment')}
              placeholder={t('cmrlist.reject.comment.placeholder', 'Explain why this request is being rejected')}
              rows={4}
            />
          </FormField>
        </form>

        <DialogFooter className="gap-3 sm:gap-3">
          <ActionButton type="button" intent="dismiss" onClick={onClose} disabled={isSubmitting}>
            {t('gen.cancel.label', 'Cancel')}
          </ActionButton>
          <ActionButton
            type="submit"
            form="reject-service-request-form"
            intent="danger-confirm"
            disabled={isSubmitting}>
            {isSubmitting ? <Spinner className="mr-2 h-4 w-4" /> : <XCircle className="mr-2 h-4 w-4" />}
            {t('cmrlist.reject.submit', 'Reject')}
          </ActionButton>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
